import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { HiOutlineFilter, HiChevronDoubleLeft, HiChevronLeft, HiChevronRight, HiChevronDoubleRight, HiOutlineChatAlt2, HiX, HiOutlineBookmark, HiBookmark } from 'react-icons/hi';
import { HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { BrandData, BrandNote, fetchBrandNotes } from '../../services/brandService';
import BrandLogo from './BrandLogo';
import Loader from '../Loader/Loader';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import BrandNotesDialog from './BrandNotesDialog';
import SearchSuggestions from '../SearchSuggestions/SearchSuggestions';
import Tooltip from '../Tooltip/Tooltip';
import { trackStoreEvent } from '../../utils/posthogUtils';
import './BrandsTable.css';

interface BrandTableData {
  id: string;
  brandName: string;
  logoPath: string;
  totalSites: number;
  sitesAcquired: number;
  totalOpp: number; // Sum of all five recommendations
  purchek: number; // purchek_recommended count
  cartContainment: number; // containment_recommended count
  cartManager: number; // cart_manager_recommended count
  estimatedCartsPerStore: number; // estimated_no_of_carts_per_store from brand table
  subscription: number;
  pairedWheel: number; // paired_wheel_recommended count
  purchekAcquired: number;
  cartContainmentAcquired: number;
  cartManagerAcquired: number;
  subscriptionAcquired: number;
  pairedWheelAcquired: number;
  estCarts: number;
  estimatedRevenue: string;
  notesCount: number;
}

interface BrandsTableProps {
  data: BrandData[];
  loading: boolean;
  offset: number;
  pageSize: number;
  totalCount: number;
  onOffsetChange: (offset: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onBrandClick: (brand: BrandData) => void;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  sortBy?: string;
  onSortChange?: (value: string) => void;
  onFilterClick?: () => void;
  onNotesRefetch?: () => void;
  activeFilterCount?: number;
  onAddToListClick?: (brandName: string) => void;
  savedBrands?: Set<string>;
  userNotesCount?: Map<string, number>;
}

// Transform BrandData to table format
const transformBrandData = (brands: BrandData[], userNotesCount?: Map<string, number>): BrandTableData[] => {
  return brands.map((brand) => {
    const totalSites = brand.total_pois?.aggregate?.count || 0;
    const sitesAcquired = brand.customers?.aggregate?.count || 0;
    const purchekRecommended = brand.purchek_recommended?.aggregate?.count || 0;
    const containmentRecommended = brand.containment_recommended?.aggregate?.count || 0;
    const cartManagerRecommended = brand.cart_manager_recommended?.aggregate?.count || 0;
    const subscriptionRecommended = brand.subscription_recommended?.aggregate?.count || 0;
    const pairedWheelRecommended = brand.paired_wheel_recommended?.aggregate?.count || 0;
    const totalOpp = purchekRecommended + containmentRecommended + cartManagerRecommended + subscriptionRecommended + pairedWheelRecommended; // Sum of all five recommendations
    const purchekAcquired = brand.purchek_acquired?.aggregate?.count || 0;
    const containmentAcquired = brand.containment_acquired?.aggregate?.count || 0;
    const cartManagerAcquired = brand.cart_manager_acquired?.aggregate?.count || 0;
    const subscriptionAcquired = brand.subscription_acquired?.aggregate?.count || 0;
    const pairedWheelAcquired = brand.paired_wheel_acquired?.aggregate?.count || 0;
    const notesCount = userNotesCount?.get(brand.name || '') || 0;

    // Return data with recommended counts
    return {
      id: brand.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
      brandName: brand.name || 'Unknown Brand',
      logoPath: '/logo192.png',
      totalSites,
      sitesAcquired,
      totalOpp, // Sum of all recommendations
      purchek: purchekRecommended, // purchek_recommended count
      cartContainment: containmentRecommended, // containment_recommended count
      cartManager: cartManagerRecommended, // cart_manager_recommended count
      estimatedCartsPerStore: brand.estimated_no_of_carts_per_store || 0, // estimated carts per store from brand table
      subscription: subscriptionRecommended, // subscription_recommended count
      pairedWheel: pairedWheelRecommended, // paired_wheel_recommended count
      purchekAcquired,
      cartContainmentAcquired: containmentAcquired,
      cartManagerAcquired,
      subscriptionAcquired,
      pairedWheelAcquired,
      estCarts: 0, // Show "-" in UI
      estimatedRevenue: '-',
      notesCount,
    };
  });
};

// Render a product cell matching the design mockup
const renderProductCell = (
  row: any,
  fullName: string,
  recommended: number,
  acquired: number,
) => {
  const { totalSites } = row.original;
  const maxVal = Math.max(totalSites, 1);
  const acquiredPct = Math.min(Math.round((acquired / maxVal) * 100), 100);
  const recommendedPct = Math.min(Math.round((recommended / maxVal) * 100), 100);
  const tooltipContent = (
    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>{fullName}</div>
      <div>Installed: <strong>{acquired.toLocaleString()}</strong></div>
      <div>Recommended: <strong>{recommended.toLocaleString()}</strong></div>
      <div>Total Sites: <strong>{totalSites.toLocaleString()}</strong></div>
    </div>
  );
  return (
    <Tooltip content={tooltipContent} side="top">
      <div className="product-cell">
        <div className="product-cell-bar">
          <div className="bar-fill-opportunity" style={{ width: `${recommendedPct}%` }} />
        </div>
        <div className="product-bar-label">
          <span className="bar-label opportunity-label">Opportunities</span>
          <span className="bar-value opportunity-value">{recommended.toLocaleString()}</span>
        </div>
        <div className="product-cell-bar">
          <div className="bar-fill-installed" style={{ width: `${acquiredPct}%` }} />
        </div>
        <div className="product-bar-label">
          <span className="bar-label installed-label">Installed</span>
          <span className="bar-value installed-value">{acquired.toLocaleString()}</span>
        </div>
      </div>
    </Tooltip>
  );
};

// Column definitions factory - will be filtered based on visibility
const createAllColumns = (onNotesClick: (brandName: string) => void, onAddToListClick?: (brandName: string) => void, savedBrands?: Set<string>): ColumnDef<BrandTableData>[] => [
  {
    id: 'brandName',
    accessorKey: 'brandName',
    header: 'Brand',
    cell: ({ row }: any) => {
      const { totalSites, sitesAcquired, brandName } = row.original;
      const pct = totalSites > 0 ? Math.round((sitesAcquired / totalSites) * 100) : 0;
      return (
        <div className="brand-cell">
          <BrandLogo brandName={brandName} className="brand-logo" />
          <div className="brand-info">
            <div className="brand-name-row">
              <span className="brand-name">{brandName}</span>
              {row.original.notesCount > 0 && (
                <HiOutlineChatAlt2 className="brand-indicator-icon" title={`${row.original.notesCount} note${row.original.notesCount > 1 ? 's' : ''}`} />
              )}
              {savedBrands?.has(brandName) && (
                <HiBookmark className="brand-indicator-icon brand-indicator-saved" title="Saved to list" />
              )}
            </div>
            <span className="brand-sites">{sitesAcquired.toLocaleString()} / {totalSites.toLocaleString()}</span>
            <span className="brand-sites-label">Sites Acquired</span>
            <span className="brand-pct-badge">{pct}%</span>
          </div>
          <div className="brand-hover-actions">
            <button
              className="brand-hover-btn"
              title="Add Notes"
              onClick={(e) => {
                e.stopPropagation();
                onNotesClick(brandName);
              }}
            >
              <HiOutlineChatAlt2 size={14} />
              <span>Notes</span>
              {row.original.notesCount > 0 && (
                <span className="notes-badge">{row.original.notesCount}</span>
              )}
            </button>
            <button
              className={`brand-hover-btn ${savedBrands?.has(brandName) ? 'saved' : ''}`}
              title="Save to List"
              onClick={(e) => {
                e.stopPropagation();
                onAddToListClick?.(brandName);
              }}
            >
              {savedBrands?.has(brandName) ? (
                <><HiBookmark size={14} /><span>Saved</span></>
              ) : (
                <><HiOutlineBookmark size={14} /><span>Save</span></>
              )}
            </button>
          </div>
        </div>
      );
    },
    size: 160,
  },
  {
    id: 'purchek',
    accessorKey: 'purchek',
    header: ({ column }: any) => (
      <div
        className="header-cell-content clickable"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Purchek
        <span className="sort-icon">
          {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : '⇅'}
        </span>
      </div>
    ),
    cell: ({ row }: any) => renderProductCell(row, 'Purchek', row.original.purchek, row.original.purchekAcquired),
    size: 150,
  },
  {
    id: 'cartContainment',
    accessorKey: 'cartContainment',
    header: ({ column }: any) => (
      <div
        className="header-cell-content clickable"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Cart Containment
        <span className="sort-icon">
          {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : '⇅'}
        </span>
      </div>
    ),
    cell: ({ row }: any) => renderProductCell(row, 'Cart Containment', row.original.cartContainment, row.original.cartContainmentAcquired),
    size: 170,
  },
  {
    id: 'cartManager',
    accessorKey: 'cartManager',
    header: ({ column }: any) => (
      <div
        className="header-cell-content clickable"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Cart Manager
        <span className="sort-icon">
          {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : '⇅'}
        </span>
      </div>
    ),
    cell: ({ row }: any) => renderProductCell(row, 'Cart Manager', row.original.cartManager, row.original.cartManagerAcquired),
    size: 150,
  },
  {
    id: 'subscription',
    accessorKey: 'subscription',
    header: ({ column }: any) => (
      <div
        className="header-cell-content clickable"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Subscription
        <span className="sort-icon">
          {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : '⇅'}
        </span>
      </div>
    ),
    cell: ({ row }: any) => renderProductCell(row, 'Subscription', row.original.subscription, row.original.subscriptionAcquired),
    size: 150,
  },
  {
    id: 'pairedWheel',
    accessorKey: 'pairedWheel',
    header: ({ column }: any) => (
      <div
        className="header-cell-content clickable"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Paired Wheel
        <span className="sort-icon">
          {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : '⇅'}
        </span>
      </div>
    ),
    cell: ({ row }: any) => renderProductCell(row, 'Paired Wheel', row.original.pairedWheel, row.original.pairedWheelAcquired),
    size: 150,
  },
  {
    id: 'estCarts',
    accessorKey: 'estCarts',
    header: ({ column }: any) => (
      <div
        className="header-cell-content clickable"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Est. Total Carts
        <span className="sort-icon">
          {column.getIsSorted() === 'asc' ? '↑' : column.getIsSorted() === 'desc' ? '↓' : '⇅'}
        </span>
      </div>
    ),
    cell: ({ row }: any) => {
      return <div className="number-cell">{row.original.estimatedCartsPerStore > 0 ? row.original.estimatedCartsPerStore : '-'}</div>;
    },
    size: 110,
  },
];

export function BrandsTable({
  data,
  loading,
  offset,
  pageSize,
  totalCount,
  onOffsetChange,
  onPageSizeChange,
  onBrandClick,
  searchTerm = '',
  onSearchChange,
  sortBy = 'Top Retailers',
  onSortChange,
  onFilterClick,
  onNotesRefetch,
  activeFilterCount = 0,
  onAddToListClick,
  savedBrands,
  userNotesCount,
}: BrandsTableProps) {
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedBrandForNotes, setSelectedBrandForNotes] = useState('');
  const [brandNotes, setBrandNotes] = useState<BrandNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [sorting, setSorting] = useState<SortingState>([]);

  // Track brand search in PostHog (debounced)
  useEffect(() => {
    if (!searchTerm.trim()) return;
    const timer = setTimeout(() => {
      trackStoreEvent.searchStores(searchTerm.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleNotesClick = useCallback(async (brandName: string) => {
    setSelectedBrandForNotes(brandName);
    setBrandNotes([]);
    setNotesLoading(true);
    setNotesDialogOpen(true);

    try {
      const notes = await fetchBrandNotes(brandName);
      setBrandNotes(notes);
    } catch (error) {
      console.error('Error loading notes:', error);
      setBrandNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, []);

  const handleNoteAdded = async () => {
    // Refetch notes for the selected brand to update the count
    try {
      const notes = await fetchBrandNotes(selectedBrandForNotes);
      setBrandNotes(notes);

      // Trigger parent component to refetch brand data so the badge count updates
      onNotesRefetch?.();
    } catch (error) {
      console.error('Error refreshing notes:', error);
    }
  };

  const filteredData = useMemo(() => {
    let result = [...data];

    if (sortBy === 'Brand Name (A-Z)') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'Brand Name (Z-A)') {
      result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (sortBy === 'Total Carts') {
      result.sort((a, b) => (b.estimated_no_of_carts_per_store || 0) - (a.estimated_no_of_carts_per_store || 0));
    }

    return result;
  }, [data, sortBy]);

  const tableData = useMemo(() => {
    const transformed = transformBrandData(filteredData, userNotesCount);
    // Override notesCount for the brand whose notes are currently loaded,
    // so the badge updates immediately without waiting for a full refetch
    if (selectedBrandForNotes && brandNotes) {
      const idx = transformed.findIndex(b => b.brandName === selectedBrandForNotes);
      if (idx !== -1) {
        transformed[idx] = { ...transformed[idx], notesCount: brandNotes.length };
      }
    }
    return transformed;
  }, [filteredData, selectedBrandForNotes, brandNotes, userNotesCount]);
  const currentPage = Math.floor(offset / pageSize);
  const pageCount = Math.ceil(totalCount / pageSize);

  // Create columns
  const columns = useMemo(() => {
    const allColumns = createAllColumns(handleNotesClick, onAddToListClick, savedBrands);
    return allColumns;
  }, [handleNotesClick, onAddToListClick, savedBrands]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), // Add sorted row model
    state: {
      sorting, // Add sorting to state
    },
    onSortingChange: setSorting, // Add sorting change handler
    manualPagination: true,
    pageCount,
  });
  const hasNextPage = (offset + pageSize) < totalCount;
  const hasPreviousPage = offset > 0;

  return (
    <div className="brands-table-wrapper">
      {/* Search and Filter Header */}
      <div className="table-search-header">
        <div className="brands-search-box">
          <HiOutlineMagnifyingGlass className="brands-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => {
              onSearchChange?.(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="brands-search-input"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={() => {
                onSearchChange?.('');
                setShowSuggestions(false);
                if (searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              }}
              title="Clear search"
              type="button"
            >
              <HiX size={16} />
            </button>
          )}
          <SearchSuggestions
            searchInput={searchTerm}
            onSelectSuggestion={(suggestion) => {
              onSearchChange?.('');
              setShowSuggestions(false);
              if (searchInputRef.current) {
                searchInputRef.current.blur();
              }
            }}
            isOpen={showSuggestions && searchTerm.length > 0}
          />
        </div>

        <div className="header-controls">
          <button className="common-btn" onClick={onFilterClick} title="Filters & Config">
            <HiOutlineFilter />
            Filters
            {activeFilterCount > 0 && (
              <span className="filter-count-badge">{activeFilterCount}</span>
            )}
          </button>

          <CustomDropdown
            options={['Brand Name (A-Z)', 'Brand Name (Z-A)', 'Total Carts']}
            value={sortBy}
            onChange={(value) => onSortChange?.(value)}
            className="sort-dropdown"
          />
        </div>
      </div>


      <div className="table-container">
        {loading ? (
          <Loader />
        ) : data.length === 0 ? (
          <div className="empty-state">No brands found</div>
        ) : (
          <table className="brands-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup: any) => (
                <tr key={headerGroup.id} className="table-header-row">
                  {headerGroup.headers.map((header: any) => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className="table-header-cell"
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row: any) => (
                <tr
                  key={row.id}
                  className="table-body-row"
                  onClick={() => onBrandClick(filteredData[parseInt(row.id)])}
                >
                  {row.getVisibleCells().map((cell: any) => (
                    <td
                      key={cell.id}
                      style={{ width: cell.column.columnDef.size }}
                      className="table-body-cell"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="table-pagination">
        <div className="pagination-left">
          <label>Rows per page: </label>
          <CustomDropdown
            options={['10', '25', '50', '100']}
            value={String(pageSize)}
            onChange={(value) => {
              onPageSizeChange(Number(value));
              onOffsetChange(0);
            }}
            className="page-size-dropdown"
          />
        </div>

        <span className="page-info">
          Page {currentPage + 1} of {pageCount || 1}
        </span>

        <div className="pagination-buttons">
          <button
            onClick={() => onOffsetChange(0)}
            disabled={!hasPreviousPage}
            className="pagination-btn"
            title="First page"
          >
            <HiChevronDoubleLeft />
          </button>
          <button
            onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
            disabled={!hasPreviousPage}
            className="pagination-btn"
            title="Previous page"
          >
            <HiChevronLeft />
          </button>
          <button
            onClick={() => onOffsetChange(offset + pageSize)}
            disabled={!hasNextPage}
            className="pagination-btn"
            title="Next page"
          >
            <HiChevronRight />
          </button>
          <button
            onClick={() => onOffsetChange((pageCount - 1) * pageSize)}
            disabled={!hasNextPage}
            className="pagination-btn"
            title="Last page"
          >
            <HiChevronDoubleRight />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="table-legend">
        <span className="legend-item">
          <span className="legend-dot legend-dot-installed" />
          Installed (Already have the solution)
        </span>
        <span className="legend-item">
          <span className="legend-dot legend-dot-opportunity" />
          Recommended Opportunity (Not yet installed)
        </span>
      </div>

      <BrandNotesDialog
        isOpen={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        brandName={selectedBrandForNotes}
        notes={brandNotes}
        isLoading={notesLoading}
        onNoteAdded={handleNoteAdded}
      />
    </div>
  );
}

export default BrandsTable;